import React, { useState, useRef, useEffect } from "react";
import classnames from "classnames";
import Str from "shared/components/Str";
import _str from "shared/util/_str";
import CodeInput from "shared/components/CodeInput";
import PropTypes from "prop-types";
import SmsStatus from "apps/login/components/screens/SmsStatus";
import useValidateCode from "shared/hooks/useValidateCode";
import useSendCode, { showSendCodeFeedbackBanner } from "shared/hooks/useSendCode";
import { trackSmsCodeReSent, trackSmsCodeVertificationResponse } from "analytics/sms";
import get from "lodash/get";
import FeedbackBanner from "shared/util/FeedbackBanner";

import "./Sms.scss";

const isInvalidOtpError = (err) => get(err || {}, "graphQLErrors.[0].extensions.code") === 16;

const EnterCode = ({ phoneNumber, handleSuccess, goBackToPhone, showContactSupport }) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccessful, setIsSuccessful] = useState(false);
	const [hasInvalidOtpError, setInvalidOtpError] = useState(false);
	const [code, setCode] = useState("");
	const [showCodeSentSuccess, setShowCodeSentSuccess] = useState(false);
	const { sendCode, canSendCode, isSendingCode } = useSendCode({ disableLeadingSend: true });
	const { validateCode } = useValidateCode();
	const codeSentTimer = useRef();

	const handleDigitChange = (digits) => {
		const nextCode = digits.map(({ value }) => value).join("");
		setCode(nextCode);
	};

	const submitOTPCode = (e) => {
		e.preventDefault();

		setIsSubmitting(true);

		validateCode({ phoneNumber, code })

			// resolve handleSuccess in case the parent wants to do something
			// before we set isSubmitting to false, like updating a phone number
			.then((tspRefreshToken) => handleSuccess(tspRefreshToken, phoneNumber))
			.then(() => {
				setIsSubmitting(false);
				setIsSuccessful(true);
				trackSmsCodeVertificationResponse({
					success: true,
					existingUser: true,
				});
			})
			.catch((err) => {
				setIsSubmitting(false);

				if (isInvalidOtpError(err)) {
					setInvalidOtpError(true);
				} else if (err && err.goBackToPhone) {
					goBackToPhone(err.statusCode);
				} else {
					// show a feedback banner for non invalid otp errors
					// but not if the error has goBackToPhone prop
					// since the useUpdatePhone hook handles it's own error
					FeedbackBanner.open({
						message: _str("Unable to validate code"),
						type: "error",
						timeout: 3000,
					});
				}

				trackSmsCodeVertificationResponse({
					success: false,
					error: err,
					existingUser: true,
				});
			});
	};

	const resendOTPCode = () => {
		if (isSendingCode || !canSendCode) {
			return;
		}

		trackSmsCodeReSent();

		sendCode(phoneNumber)
			.then(() => setShowCodeSentSuccess(true))
			.catch(showSendCodeFeedbackBanner);
	};

	useEffect(() => {
		if (showCodeSentSuccess && !isSendingCode) {
			clearTimeout(codeSentTimer.current);

			codeSentTimer.current = setTimeout(() => {
				setShowCodeSentSuccess(false);
				codeSentTimer.current = null;
			}, 5000);
		}
	}, [showCodeSentSuccess, isSendingCode]);

	// clear the timer when the component unmounts
	useEffect(() => () => clearTimeout(codeSentTimer.current), []);

	const showStatusScreen = isSubmitting || isSuccessful;

	return (
		<>
			<div className="sms-header">
				{!showStatusScreen &&
					<span className="sms-header-title sms-header-title-phone">
						<Str>Enter the code we sent to your phone number</Str>
					</span>}
			</div>
			<div className="sms-fields-container">
				{!showStatusScreen &&
					<form className="oknf oknf--gray sms-form sms">
						<div className="sms-fields sms-fields-code">
							<div className={classnames("sms-code-sent", {
								"sms-code-sent-hide": !showCodeSentSuccess,
							})}>
								<div className="sms-code-sent-success">
									<i className="sms-code-sent-success-icon" />
								</div>
								<Str>Code sent!</Str>
							</div>
							<CodeInput
								handleDigitChange={handleDigitChange}
								showError={hasInvalidOtpError}
							/>
						</div>

						<div className="sms-actions">
							<button
								type="submit"
								disabled={code.length < 6}
								className={classnames("sms-actions-button", {
									"isSubmitting": isSubmitting,
								})}
								onClick={submitOTPCode}
							>
								{isSubmitting ? _str("Submitting…") : _str("Next")}
							</button>
						</div>

						<div className="sms-resend">
							<button
								type="button"
								className={classnames("sms-fields-field-link", {
									"sending": isSendingCode,
								})}
								disabled={!canSendCode}
								onClick={resendOTPCode}
							>
								{isSendingCode ? _str("Sending Code") : _str("Resend Code")}
							</button>

							{showContactSupport && (
								<>
									<span className="or">or</span>
									<a
										className="sms-fields-field-link sms-fields-field-link-support"
										href="https://help.okcupid.com/article/202-sms-verification"
										target="_blank"
										rel="noopener noreferrer"
									>
										<Str>Contact Support</Str>
									</a>
								</>
							)}
						</div>

					</form>
				}
				{isSubmitting && <SmsStatus loading message={_str("Validating Code")} />}
				{isSuccessful && <SmsStatus message={_str("Success!")} />}
			</div>
		</>
	);
};

EnterCode.propTypes = {
	phoneNumber: PropTypes.string.isRequired,
	handleSuccess: PropTypes.func.isRequired,
	showContactSupport: PropTypes.bool,
	goBackToPhone: PropTypes.func.isRequired,
};

EnterCode.defaultProps = {
	showContactSupport: false,
};

export default EnterCode;



// WEBPACK FOOTER //
// ./src/shared/components/Sms/EnterCode.jsx